import { Add } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import {
  ArrayPath,
  FieldArray,
  FieldArrayPath,
  FieldValues,
  useFieldArray,
  UseFieldArrayUpdate,
  useFormContext,
} from "react-hook-form";
import withDragDropContext from "hocs/withDragDropContext";
import FieldsList from "./List";

const FieldsListWithDragDropContext = withDragDropContext(FieldsList);

export interface FieldsArrayListProps<TFieldValues extends FieldValues = FieldValues> {
  disabled?: boolean;
  fieldName: FieldArrayPath<TFieldValues>;
  label: string;
  totalCountLabel: string;
  valueKeyName?: keyof TFieldValues;
  addButtonText: string;
  keyName?: string;
  renderFieldControlsComponent: (
    index: number,
    update: UseFieldArrayUpdate<TFieldValues, FieldArrayPath<TFieldValues>>
  ) => ReactNode;
  withDragNDrop?: boolean;
}

const FieldsArrayList = <TFieldValues extends FieldValues = FieldValues>({
  disabled,
  fieldName,
  label,
  totalCountLabel,
  valueKeyName = "value",
  addButtonText,
  keyName = "formId",
  renderFieldControlsComponent,
  withDragNDrop = false,
}: FieldsArrayListProps<TFieldValues>) => {
  const { control } = useFormContext<TFieldValues>();
  const { fields, append, move, update, remove } = useFieldArray({
    control,
    keyName,
    name: fieldName,
  });

  const fieldsListBaseProps = {
    renderFieldControlsComponent,
    disabled,
    keyName,
    fields,
    remove,
    update,
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Stack
          direction='row'
          spacing={2}
          justifyContent='space-between'
          alignItems='flex-end'>
          <Typography
            variant='body14rg'
            color='text.secondary'>
            {label}
          </Typography>
          <Typography
            variant='body16rg'
            color='primary.main'
            sx={{ py: 1, px: 2, borderRadius: 2, backgroundColor: "bgSwitch" }}>
            {totalCountLabel}: {fields.length}
          </Typography>
        </Stack>
        {withDragNDrop ? (
          // @ts-expect-error проблема типизации из-за хока withDragDropContext
          <FieldsListWithDragDropContext
            droppableId={fieldName}
            moveItems={move}
            withDragNDrop
            {...fieldsListBaseProps}
          />
        ) : (
          <FieldsList {...fieldsListBaseProps} />
        )}
      </Stack>
      <Button
        disabled={disabled}
        startIcon={<Add />}
        sx={({ typography }) => ({
          px: 0,
          justifyContent: "flex-start",
          color: "base.200",
          ...typography.body14rg,
        })}
        onClick={() => append({ [valueKeyName]: "" } as FieldArray<TFieldValues, ArrayPath<TFieldValues>>)}>
        {addButtonText}
      </Button>
    </Box>
  );
};

export default FieldsArrayList;
